import { Link } from "react-router";
import { useCockpit } from "@/api/hooks/admin";
import type { CockpitAction, CockpitMetric } from "@/api/endpoints";
import { fmtDate, humanize } from "@/lib/format";

export function AdminOverviewPage() {
  const { data, isLoading } = useCockpit();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Cockpit</h1>
        <p className="mt-1 text-sm text-text-muted">
          The daily queue for running NGON from one place.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {isLoading && !data
          ? Array.from({ length: 5 }).map((_, i) => (
              <MetricCard key={i} metric={{ label: "Loading", value: 0, tone: "neutral" }} loading />
            ))
          : data?.metrics.map((metric) => (
              <MetricCard key={metric.label} metric={metric} />
            ))}
      </div>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="rounded-2xl border border-border bg-surface">
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-base font-semibold">Next actions</h2>
            <p className="mt-1 text-sm text-text-muted">
              Process from top to bottom; each item should end with a decision or a clear next step.
            </p>
          </div>

          <div className="divide-y divide-border">
            {isLoading && !data ? (
              <EmptyState>Loading queue…</EmptyState>
            ) : !data?.next_actions.length ? (
              <EmptyState>No open actions. Check leads or source one candidate.</EmptyState>
            ) : (
              data.next_actions.map((action) => (
                <ActionRow key={action.id} action={action} />
              ))
            )}
          </div>
        </div>

        <aside className="rounded-2xl border border-border bg-surface p-5">
          <h2 className="text-base font-semibold">Operating rules</h2>
          <div className="mt-4 flex flex-col gap-3">
            {(data?.operating_notes ?? [
              "Keep one sourcing/evaluation thread active at a time.",
              "External outreach requires approval.",
              "Clarify missing geo, budget, consent, and availability first.",
            ]).map((note) => (
              <div
                key={note}
                className="rounded-lg border border-border bg-surface-alt px-3.5 py-3 text-sm leading-6 text-text-muted"
              >
                {note}
              </div>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
}

function MetricCard({
  metric,
  loading = false,
}: {
  metric: CockpitMetric;
  loading?: boolean;
}) {
  const toneClass =
    metric.tone === "warning"
      ? "bg-warning-soft text-[#854d0e]"
      : metric.tone === "success"
        ? "bg-success-soft text-[#166534]"
        : metric.tone === "danger"
          ? "bg-danger-soft text-[#991b1b]"
          : "bg-surface-alt text-text-muted";

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-text-muted">
        {loading ? "Loading" : metric.label}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-3xl font-bold tracking-tight">
          {loading ? "—" : metric.value}
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${toneClass}`}>
          {loading ? "sync" : metric.tone}
        </span>
      </div>
    </div>
  );
}

function ActionRow({ action }: { action: CockpitAction }) {
  const priorityClass =
    action.priority === "high"
      ? "bg-danger-soft text-[#991b1b]"
      : action.priority === "medium"
        ? "bg-warning-soft text-[#854d0e]"
        : "bg-surface-alt text-text-muted";

  return (
    <Link
      to={action.href}
      className="grid gap-3 px-5 py-4 hover:bg-surface-alt md:grid-cols-[150px_minmax(0,1fr)_150px]"
    >
      <div className="flex flex-wrap items-center gap-2 md:block">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${priorityClass}`}>
          {action.priority}
        </span>
        <div className="mt-0 text-xs text-text-muted md:mt-2">
          {humanize(action.kind, "service")}
        </div>
      </div>

      <div className="min-w-0">
        <div className="font-semibold">{action.title}</div>
        {action.subtitle && (
          <div className="mt-1 text-sm text-text-muted">{action.subtitle}</div>
        )}
        <div className="mt-2 text-sm leading-6">{action.suggested_action}</div>
      </div>

      <div className="text-left md:text-right">
        <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">
          {action.status}
        </div>
        <div className="mt-1 text-sm text-text-muted">{fmtDate(action.created_at)}</div>
      </div>
    </Link>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <div className="px-5 py-10 text-center text-sm text-text-muted">{children}</div>;
}

